import { FC, useEffect, useRef, useState } from 'react';

import { Box, HStack, Icon, Text, useBreakpointValue } from '@chakra-ui/react';
import { AudioLines, Mic, PhoneOff, Square } from 'lucide-react';

import { IconButton, WorkBeam } from '@/components/core';
import { snackbar } from '@/components/features/Snackbar';
import { getAgentMessageLimit } from '@/constant/agentMessageLimits';
import { cn } from '@/lib/utils';
import {
  clearPendingPromptChip,
  peekPendingPromptChip,
  PENDING_PROMPT_CHIP_EVENT,
} from '@/utils/pendingPromptChip';

import {
  applyValues,
  Editor,
  EditorHandle,
  fillNames,
  listFor,
  PromptChipInput,
  PromptUse,
  SuggestionItemGroup,
  templateBodyOf,
  usePromptSubject,
} from '../Editor';
import { CommandPicker } from './CommandPicker';
import { SendMessageIcon } from './SendMessageIcon';
import { VoiceCallBar, VoiceCallControl } from './VoiceCallBar';

const VOICE_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
];

const pickSupportedMimeType = () => {
  if (
    typeof MediaRecorder === 'undefined' ||
    typeof MediaRecorder.isTypeSupported !== 'function'
  ) {
    return '';
  }

  return (
    VOICE_MIME_CANDIDATES.find(type => MediaRecorder.isTypeSupported(type)) ??
    ''
  );
};

const formatRecordingDuration = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const EMPTY_SUGGESTIONS: SuggestionItemGroup[] = [];

const formatCount = (count: number) => count.toLocaleString('en-GB');

type Props = {
  className?: string;
  isLoading?: boolean;
  placeholder?: string;
  /**
   * `prompts` is the Prompt Templates record of the message's chips, with ranges in
   * `message` (spec FSI #1226). Surfaces that do not store it can ignore it.
   */
  onSendMessage?: (message: string, prompts: PromptUse[]) => void;
  suggestions?: SuggestionItemGroup[];
  enableVoice?: boolean;
  onSendVoiceNote?: (blob: Blob) => void;
  /** Hands-free call with the agent, beside the voice-note button. */
  voiceCall?: VoiceCallControl;
  onAbort?: () => void;
  /** The agent this composer sends to; sets its message limit, if any. */
  agentName?: string;
  /** The agent's id: the `/` and `+` menus show its Recent row (#596). */
  agentId?: number;
  /**
   * This composer's key for a chip left by Use prompt on the Prompts page
   * (`pendingPromptChipKey`). The chip is inserted once, unsent, then cleared.
   */
  pendingPromptKey?: string;
};

const PromptInputWithActions: FC<Props> = ({
  className,
  isLoading = false,
  placeholder,
  onSendMessage,
  suggestions = EMPTY_SUGGESTIONS,
  enableVoice = false,
  onSendVoiceNote,
  voiceCall,
  onAbort,
  agentName,
  agentId,
  pendingPromptKey,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [input, setInput] = useState('');
  const [prompts, setPrompts] = useState<PromptUse[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<EditorHandle>(null);
  const suggestionRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const shouldSendRecordingRef = useRef(false);

  const stopMediaTracks = () => {
    mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    mediaStreamRef.current = null;
  };

  const resetRecorder = () => {
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    shouldSendRecordingRef.current = false;
    setIsRecording(false);
    setRecordingSeconds(0);
    stopMediaTracks();
  };

  useEffect(() => {
    if (!isRecording) return;

    const timer = window.setInterval(() => {
      setRecordingSeconds(seconds => seconds + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isRecording]);

  useEffect(
    () => () => {
      shouldSendRecordingRef.current = false;
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop();
      }
      stopMediaTracks();
    },
    []
  );

  // What this chat knows for Context Variables (#632), read by the landing
  // below through a ref so a late answer does not restart it.
  const { context: promptContext, settled: promptContextSettled } =
    usePromptSubject();
  const promptContextRef = useRef(promptContext);
  promptContextRef.current = promptContext;

  // Use prompt on the Prompts page (VH3_CONNECT #593): once the composer is
  // ready, insert the chip left for this composer, then clear it. The chip is
  // cleared only when the editor holds one more chip of that template than
  // before the insert, never on matching text. An open composer takes a later
  // chip from the event.
  // A chip with Context Variables the page left empty (#632) waits for this
  // chat's lookup, takes what it found, and opens its fill step.
  useEffect(() => {
    if (!pendingPromptKey || isLoading) return;
    let tries = 0;
    let timer: number | undefined;
    const take = () => {
      const chip = peekPendingPromptChip(pendingPromptKey);
      if (!chip) return;
      const editor = editorRef.current;
      const body = templateBodyOf(chip);
      const names = listFor(body, promptContextRef.current);
      const waitsForLookup =
        !promptContextSettled && /\{[\w-]+\.[\w.-]+\}/.test(body);
      if (editor && chip.templateId != null && !waitsForLookup) {
        const listed = new Set(names.map(item => item.name));
        const empty = fillNames(body, names).filter(
          name => listed.has(name) && !chip.values?.[name]?.trim()
        );
        let landing = chip;
        if (empty.length > 0) {
          const values = { ...chip.values };
          empty.forEach(name => {
            const found = promptContextRef.current.values[name];
            if (found?.trim()) values[name] = found;
          });
          landing = {
            ...chip,
            values,
            body: applyValues(body, values, names),
          };
        }
        const before = editor.countPromptChips(chip.templateId);
        editor.insertSlashCommand(landing);
        if (editor.countPromptChips(chip.templateId) > before) {
          clearPendingPromptChip(pendingPromptKey);
          if (empty.length > 0) {
            // Provisional while a field is still empty: closing the step
            // without Insert takes the chip out again.
            editor.openPromptChip(chip.templateId, {
              provisional: empty.some(name => !landing.values?.[name]?.trim()),
            });
          }
          return;
        }
      }
      if (tries++ > 40) return;
      timer = window.setTimeout(take, 120);
    };
    timer = window.setTimeout(take, 0);
    const onLeft = (event: Event) => {
      const key = (event as CustomEvent<{ key?: string }>).detail?.key;
      if (key !== pendingPromptKey) return;
      tries = 0;
      window.clearTimeout(timer);
      timer = window.setTimeout(take, 0);
    };
    window.addEventListener(PENDING_PROMPT_CHIP_EVENT, onLeft);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(PENDING_PROMPT_CHIP_EVENT, onLeft);
    };
  }, [pendingPromptKey, isLoading, promptContextSettled]);

  const handleEditorChange = (content: string, record: PromptUse[]) => {
    setInput(content);
    setPrompts(record);
  };

  const sendMessage = (message: string) => {
    if (onSendMessage) {
      onSendMessage(message, prompts);
    }
    setInput('');
    setPrompts([]);
    if (!isMobile) {
      inputRef.current?.focus();
    }
  };

  // `input` is the composed message (chip bodies plus free text). FSI counts
  // the code points of the trimmed message, so this does too.
  const messageLimit = getAgentMessageLimit(agentName);
  const inputLength = [...input.trim()].length;
  const isOverLimit = messageLimit !== undefined && inputLength > messageLimit;

  const handleSend = () => {
    if (input.trim() && !isLoading && !isRecording && !isOverLimit) {
      sendMessage(input);
    }
  };

  const handlePrimaryClick = () => {
    if (isLoading) {
      onAbort?.();
      return;
    }
    handleSend();
  };

  const startRecording = async () => {
    if (isLoading || isRecording || !onSendVoiceNote) return;

    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      snackbar({
        title: 'Microphone unavailable',
        description: 'Voice notes are not supported in this browser.',
        type: 'error',
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickSupportedMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      shouldSendRecordingRef.current = true;

      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const shouldSend = shouldSendRecordingRef.current;
        const blobType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobType });
        resetRecorder();

        if (shouldSend && blob.size > 0) {
          onSendVoiceNote(blob);
        }
      };

      recorder.start();
      setRecordingSeconds(0);
      setIsRecording(true);
    } catch {
      resetRecorder();
      snackbar({
        title: 'Microphone access needed',
        description: 'Allow microphone access to send a voice note.',
        type: 'error',
      });
    }
  };

  const stopRecording = (shouldSend: boolean) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      resetRecorder();
      return;
    }

    shouldSendRecordingRef.current = shouldSend;
    recorder.stop();
  };

  const handleVoiceClick = () => {
    if (isRecording) {
      stopRecording(true);
      return;
    }
    void startRecording();
  };

  const isInCall = Boolean(voiceCall && voiceCall.status !== 'off');
  const editorDisabled = isLoading || isRecording || isInCall;
  const canAbort = Boolean(onAbort);
  const primaryDisabled = isLoading
    ? !canAbort
    : !input.trim() || isRecording || isInCall || isOverLimit;
  const hasCommands = suggestions.some(group => group.items.length > 0);
  const showCommandPicker = hasCommands && !isRecording && !isInCall;
  const voiceButtonCount = (enableVoice ? 1 : 0) + (voiceCall ? 1 : 0);
  const callAgentName = voiceCall?.agentName ?? 'Connie';

  const handleInsertCommand = (item: PromptChipInput) => {
    editorRef.current?.insertSlashCommand(item);
  };

  return (
    <Box
      ref={suggestionRef}
      display='flex'
      flexDirection='column'
      width='full'
      border='none'
      mb='0'
      className={cn('group/input', className)}
    >
      <WorkBeam preset='line' active={isLoading} borderRadius={12}>
        <Editor
          ref={editorRef}
          disabled={editorDisabled}
          onChange={handleEditorChange}
          value={input}
          onSubmit={handleSend}
          placeholder={isRecording || isInCall ? '' : placeholder}
          suggestions={suggestions}
          agentId={agentId}
          contentPaddingLeft={showCommandPicker ? 48 : 12}
          contentPaddingRight={48 + voiceButtonCount * 40}
        >
          {voiceCall && isInCall && <VoiceCallBar call={voiceCall} />}
          {isRecording && (
            <HStack
              position='absolute'
              insetY={0}
              left={0}
              right={0}
              px={4}
              pr='96px'
              spacing={3}
              bg='gray.800'
              borderRadius='inherit'
              pointerEvents='none'
              zIndex={2}
              aria-live='polite'
            >
              <Box
                boxSize={2}
                flexShrink={0}
                borderRadius='full'
                bg='red.400'
                className='animate-pulse'
              />
              <Text color='white' fontSize='sm' fontWeight='semibold'>
                Recording
              </Text>
              <Text
                color='whiteAlpha.700'
                fontSize='sm'
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatRecordingDuration(recordingSeconds)}
              </Text>
              <Text
                display={{ base: 'none', md: 'block' }}
                color='whiteAlpha.500'
                fontSize='xs'
              >
                Tap stop to send
              </Text>
            </HStack>
          )}
          {showCommandPicker && (
            <HStack position='absolute' left={2} bottom={2} spacing={1}>
              <CommandPicker
                suggestions={suggestions}
                agentId={agentId}
                disabled={editorDisabled}
                onSelect={handleInsertCommand}
              />
            </HStack>
          )}
          <HStack position='absolute' right={2} bottom={2} spacing={1}>
            {enableVoice && (
              <IconButton
                variant='glassy'
                size='sm'
                colorScheme={isRecording ? 'red' : 'gray'}
                zIndex={3}
                bg={isRecording ? 'red.500' : undefined}
                color={isRecording ? 'white' : undefined}
                _hover={isRecording ? { bg: 'red.400' } : undefined}
                icon={
                  isRecording ? (
                    <Square size={16} className='fill-current' />
                  ) : (
                    <Mic size={16} />
                  )
                }
                disabled={isLoading || isInCall}
                onClick={handleVoiceClick}
                aria-label={
                  isRecording ? 'Stop recording' : 'Record voice note'
                }
              />
            )}
            {voiceCall && (
              <IconButton
                variant='glassy'
                size='sm'
                colorScheme='gray'
                zIndex={3}
                icon={
                  isInCall ? <PhoneOff size={16} /> : <AudioLines size={16} />
                }
                disabled={!isInCall && (isLoading || isRecording)}
                onClick={isInCall ? voiceCall.onStop : voiceCall.onStart}
                aria-label={isInCall ? 'End call' : `Talk to ${callAgentName}`}
                title={
                  isInCall ? 'End call' : `Talk to ${callAgentName}, hands-free`
                }
              />
            )}
            <IconButton
              variant='glassy'
              size='sm'
              colorScheme='gray'
              zIndex={3}
              icon={
                isLoading ? (
                  <Square size={16} className='fill-current' />
                ) : (
                  <Icon
                    as={SendMessageIcon}
                    boxSize={3}
                    className='text-primary'
                  />
                )
              }
              disabled={primaryDisabled}
              onClick={handlePrimaryClick}
              aria-label={isLoading ? 'Stop response' : 'Send message'}
            />
          </HStack>
        </Editor>
      </WorkBeam>
      {isOverLimit && messageLimit !== undefined && (
        <Text
          mt={1.5}
          px={1}
          fontSize='xs'
          color='red.300'
          role='status'
          data-cy='composer-limit'
        >
          Too long for {agentName}: {formatCount(inputLength)} /{' '}
          {formatCount(messageLimit)} characters
        </Text>
      )}
    </Box>
  );
};

export default PromptInputWithActions;
